import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';

import landingPageTheme from '@/@mantis/theme/landingPageTheme';
import { useGetPayments } from '@/api/endpoints/payment/useGetPayments';
import { useGetStripeSubscriptions } from '@/api/endpoints/payment/useGetStripeSubscriptions';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { PublicRoutes, ResidentRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';

interface SuccessState {
  status: 'loading' | 'success' | 'error';
  message?: string;
}

// Payment interface with optional metadata
interface PaymentWithMetadata {
  id: string;
  status: string;
  metadata?: {
    checkout_session_id?: string;
    [key: string]: string | undefined;
  };
}

// Custom wrapper for the success page to use branding theme
const SuccessPageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = createTheme(landingPageTheme);

  // Add preload link for Nohemi font to improve loading
  useEffect(() => {
    const fontPreloadLinks = [
      { href: '/assets/fonts/Nohemi-Regular.woff2', type: 'font/woff2' },
      { href: '/assets/fonts/Nohemi-SemiBold.woff', type: 'font/woff' },
    ];

    fontPreloadLinks.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = font.href;
      link.as = 'font';
      link.type = font.type;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    return () => {
      fontPreloadLinks.forEach(font => {
        const links = document.querySelectorAll(`link[href="${font.href}"]`);
        links.forEach(link => link.remove());
      });
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>{children}</div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const RentSuccessPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { session } = useAuth();
  const [successState, setSuccessState] = useState<SuccessState>({ status: 'loading' });

  // Parse checkout session ID from query params
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');

  // Fetch payments and subscriptions to verify success
  const { data: payments = [], refetch: refetchPayments } = useGetPayments(
    {},
    !!session?.access_token
  );

  const { refetch: refetchSubscriptions } = useGetStripeSubscriptions({}, !!session?.access_token);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Refresh payment data
        await refetchPayments();
        await refetchSubscriptions();

        // Check if there's a successful payment matching the session ID
        const isSuccessful = payments.some(payment => {
          // Cast to our interface with metadata
          const typedPayment = payment as unknown as PaymentWithMetadata;
          return (
            typedPayment.metadata?.checkout_session_id === sessionId &&
            ['completed', 'active', 'processing'].includes(typedPayment.status)
          );
        });

        if (isSuccessful || !sessionId) {
          setSuccessState({
            status: 'success',
            message: 'Your rent payment was successfully processed.',
          });
        } else {
          setSuccessState({
            status: 'error',
            message:
              'We could not verify your payment. Please contact support if funds were withdrawn.',
          });
        }
      } catch (error) {
        setSuccessState({
          status: 'error',
          message:
            'An error occurred while verifying your payment. Please check your payment history.',
        });
      }
    };

    if (session?.access_token) {
      verifyPayment();
    } else {
      // For anonymous users, just show successful message
      setSuccessState({
        status: 'success',
        message: 'Your payment has been received.',
      });
    }
  }, [session, sessionId, refetchPayments, refetchSubscriptions, payments]);

  const navigateToPayments = () => {
    history.push(ResidentRoutes.payments);
  };

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        p: 3,
        position: 'relative',
        backgroundColor: '#000000',
        backgroundImage: 'url("/assets/images/landing/hero-bg-new.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 0,
        },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          mb: 6,
          mt: 4,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Link to={PublicRoutes.home} style={{ textDecoration: 'none' }}>
          <img src="/images/pricing/terrai-logo.svg" alt="Terrai" style={{ height: 40 }} />
        </Link>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 5 },
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(230, 235, 245, 0.3)',
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        {successState.status === 'loading' && (
          <Stack spacing={3} alignItems="center" py={4}>
            <CircularProgress color="primary" size={60} />
            <Typography variant="h5" fontWeight="500">
              Verifying your payment...
            </Typography>
          </Stack>
        )}

        {successState.status === 'success' && (
          <Stack spacing={4} alignItems="center" py={2}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
            <Typography variant="h3" fontWeight="600">
              Payment Successful!
            </Typography>
            <Typography variant="body1" fontSize="1.1rem" color="text.secondary" maxWidth="450px">
              Thank you for your payment. Your rent payment has been successfully processed.
            </Typography>

            {successState.message && (
              <Alert
                severity="info"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  '& .MuiAlert-icon': { alignItems: 'center' },
                }}
              >
                {successState.message}
              </Alert>
            )}

            {session?.access_token && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  mt: 2,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
                onClick={navigateToPayments}
              >
                View Payment History
              </Button>
            )}
          </Stack>
        )}

        {successState.status === 'error' && (
          <Stack spacing={4} alignItems="center" py={3}>
            <Alert
              severity="warning"
              sx={{
                width: '100%',
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' },
              }}
            >
              {successState.message || 'Something went wrong with your payment verification.'}
            </Alert>

            {session?.access_token && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
                onClick={navigateToPayments}
              >
                Check Payment History
              </Button>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );

  return <SuccessPageWrapper>{content}</SuccessPageWrapper>;
};

export default RentSuccessPage;
